import React from 'react'

import {Icon, Button, H4, H6, Panel, Row, S1, Colors, P} from '../UI/index.js'
import AreaMap from '../map/AreaMap.js'
import OrderConditionGroupsEditor from '../conditions/OrderConditionGroupsEditor.js'
import {PARTNER_CONDITION_VARIABLES} from '../../../server/functions/orders/orderConditions.js'
import {formSpacing, FormRow, FormStack, Toggle, ColorPicker, Input, Select, MetricInput} from '../UI/forms/index.js'
import t from '../../helpers/Languages.js'
import _ from 'lodash'

/**
 * The default settings of one direction for all partner connections: forwarding orders to partners (outgoing) or
 * receiving orders from partners (incoming).
 */
export default class DefaultPartnerSettingsPanel extends React.PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            settingsChanged: false,
            defaultPartnerSettings: structuredClone(this.props.defaultPartnerSettings)
        }
    }

    onChangeValue(path, event) {
        const defaultPartnerSettings = {...this.state.defaultPartnerSettings}
        _.set(defaultPartnerSettings, path, event.target.value)
        this.setState({defaultPartnerSettings, settingsChanged: true})
    }

    onChangeChecked(path, event) {
        const defaultPartnerSettings = {...this.state.defaultPartnerSettings}
        _.set(defaultPartnerSettings, path, !!event.target.checked)
        this.setState({defaultPartnerSettings, settingsChanged: true})
    }

    /**
     * @param {string} key
     * @param {string} value
     */
    onChangeArray(key, value) {
        const defaultPartnerSettings = {...this.state.defaultPartnerSettings}

        const array = _.get(defaultPartnerSettings, key, [])

        const index = array.indexOf(value)

        if (index > -1) {
            array.splice(index, 1)
        } else {
            array.push(value)
        }

        _.set(defaultPartnerSettings, key, array)

        this.setState({defaultPartnerSettings, settingsChanged: true})
    }

    /**
     * @param {object} geoJSON
     */
    async onCreateDeliveryZone(geoJSON) {
        const deliveryZone = await this.props.onCreateDeliveryZone(geoJSON)

        if (deliveryZone) {
            this.onChangeArray('incoming.deliveryZones', deliveryZone.id)
        }
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     * @param {Array} conditionGroups
     * @param {boolean} conditionsMatchAll
     */
    onChangeConditions(direction, conditionGroups, conditionsMatchAll) {
        const defaultPartnerSettings = {...this.state.defaultPartnerSettings}
        defaultPartnerSettings[direction] = {...defaultPartnerSettings[direction], conditionGroups, conditionsMatchAll}
        this.setState({defaultPartnerSettings, settingsChanged: true})
    }

    /**
     * @param {'outgoing' | 'incoming'} direction
     */
    renderConditions(direction) {
        const {reseller, customers} = this.props
        const settings = this.state.defaultPartnerSettings[direction]

        return (
            <>
                <H6 style={{marginBottom: 12, marginTop: 36}}>{t('conditions')}</H6>
                <P style={{marginBottom: 24}}>{direction === 'outgoing' ? t('partnerDefaultOutgoingConditionsInfo') : t('partnerDefaultIncomingConditionsInfo')}</P>

                <OrderConditionGroupsEditor
                    conditionGroups={settings.conditionGroups || []}
                    matchAll={settings.conditionsMatchAll !== false}
                    variables={PARTNER_CONDITION_VARIABLES[direction]}
                    customers={customers || {}}
                    reseller={reseller}
                    onChange={(conditionGroups, conditionsMatchAll) => this.onChangeConditions(direction, conditionGroups, conditionsMatchAll)}
                />
            </>
        )
    }

    onSubmit() {
        this.props.onSave(this.state.defaultPartnerSettings)
    }

    render() {
        const {reseller, direction} = this.props
        const {defaultPartnerSettings} = this.state

        return (
            <Panel shadow style={{flex: '1 1 0', minWidth: 600, maxWidth: 800, padding: 24, height: 'fit-content'}}>
                <Row style={{justifyContent: 'center', marginBottom: 36}}>
                    <H4>{reseller?.settings.accountName || reseller?.name}</H4>
                </Row>

                {direction === 'outgoing' &&
                    <>
                        <H6 style={{marginBottom: 24}}>{t('partnerDefaultOutgoingSection')}</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <Toggle
                                label={t('partnerShowScanColor')}
                                info={t('partnerShowScanColorInfo')}
                                checked={defaultPartnerSettings.outgoing.useCustomScanColor}
                                onChange={(event) => this.onChangeChecked('outgoing.useCustomScanColor', event)}
                            />

                            {defaultPartnerSettings.outgoing.useCustomScanColor &&
                                <Row style={{alignItems: 'center', columnGap: formSpacing.columnGap}}>
                                    <ColorPicker
                                        value={defaultPartnerSettings.outgoing.customScanColor}
                                        colors={Colors.routeColors}
                                        onChange={(event) => this.onChangeValue('outgoing.customScanColor', event)}
                                    />

                                    <P
                                        style={{
                                            height: 36,
                                            flex: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 4,
                                            backgroundColor: defaultPartnerSettings.outgoing.customScanColor || '',
                                            color: Colors.isDarkBackground(defaultPartnerSettings.outgoing.customScanColor || '') ? 'white' : 'black'
                                        }}
                                    >
                                        {reseller?.settings.accountName || reseller?.name}
                                    </P>
                                </Row>
                            }

                            <Input
                                label={t('partnerCreditorCode')}
                                info={t('partnerCreditorCodeInfo')}
                                value={defaultPartnerSettings.outgoing.creditorCode}
                                onChange={(event) => this.onChangeValue('outgoing.creditorCode', event)}
                            />
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 36}}>{t('partnerOrderSettings')}</H6>

                        <FormStack style={{marginTop: formSpacing.section, marginBottom: formSpacing.rowGap}}>
                            <Select
                                label={t('partnerCommunication')}
                                value={defaultPartnerSettings.outgoing.communication === 'reseller' ? 'reseller' : 'partner'}
                                options={[
                                    {value: 'reseller', title: reseller.settings.accountName || reseller.name},
                                    {value: 'partner', title: t('partnerPartner')}
                                ]}
                                onChange={(event) => this.onChangeValue('outgoing.communication', event)}
                            />

                            <Select
                                label={t('partnerAllowEditOrder')}
                                value={defaultPartnerSettings.outgoing.allowEditOrder || 'yes'}
                                options={[
                                    {value: 'yes', title: t('yes')},
                                    {value: 'addressOnly', title: t('partnerAddressOnly')},
                                    {value: 'no', title: t('no')}
                                ]}
                                onChange={(event) => this.onChangeValue('outgoing.allowEditOrder', event)}
                            />

                            <Toggle
                                label={t('partnerAutoForwardOrders')}
                                info={t('partnerAutoForwardOrdersInfo')}
                                checked={!!defaultPartnerSettings.outgoing.autoForwardOrders}
                                onChange={(event) => this.onChangeChecked('outgoing.autoForwardOrders', event)}
                            />
                        </FormStack>

                        {this.renderConditions('outgoing')}
                    </>
                }

                {direction === 'incoming' &&
                    <>
                        <H6 style={{marginBottom: 24}}>{t('partnerDefaultIncomingSection')}</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <Toggle
                                label={t('partnerShowRouteInfo')}
                                info={t('partnerShowRouteInfoInfo')}
                                checked={defaultPartnerSettings.incoming.showRouteInfo}
                                onChange={(event) => this.onChangeChecked('incoming.showRouteInfo', event)}
                            />
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 36}}>{t('deliveryZone')}</H6>

                        <div style={{height: 500, marginBottom: 24}}>
                            <AreaMap
                                center={reseller.settings.companyAddress.position}
                                layers={reseller.settings.deliveryZones.filter((deliveryZone) => {
                                    return defaultPartnerSettings.incoming.deliveryZones.includes(deliveryZone.id)
                                })}
                                onCreate={this.onCreateDeliveryZone.bind(this)}
                            />
                        </div>

                        <P style={{marginBottom: 24}}>
                            {t('partnerDefaultDeliveryZoneText', reseller.settings.accountName || reseller.name)}
                        </P>

                        {reseller.settings.deliveryZones.map((deliveryZone) => {
                            return (
                                <Row key={deliveryZone.id}style={{alignItems: 'center', marginBottom: 12}}>
                                    <Icon icon='mdi mdi-circle' style={{marginRight: 6, fontSize: 24, color: deliveryZone.color}} />

                                    <S1 style={{flex: 1}}>{deliveryZone.name}</S1>

                                    <Toggle
                                        style={{flex: 0}}
                                        checked={defaultPartnerSettings.incoming.deliveryZones.includes(deliveryZone.id)}
                                        onChange={() => this.onChangeArray('incoming.deliveryZones', deliveryZone.id)}
                                    />
                                </Row>
                            )
                        })}

                        <FormStack style={{marginTop: formSpacing.rowGap, marginBottom: formSpacing.rowGap}}>
                            <Input
                                label={t('partnerAdditionalPostalCodes')}
                                info={t('partnerIncomingPostalCodesInfo')}
                                value={defaultPartnerSettings.incoming.postalCodes || ''}
                                onChange={(event) => this.onChangeValue('incoming.postalCodes', event)}
                            />
                        </FormStack>

                        <H6 style={{marginBottom: 12, marginTop: 36}}>{t('partnerMaxDimensions')}</H6>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <FormRow>
                                <MetricInput
                                    label={t('partnerMaxLength')}
                                    value={defaultPartnerSettings.incoming.maxLength}
                                    divisionFactor={10}
                                    onChange={(event) => this.onChangeValue('incoming.maxLength', event)}
                                    append='cm'
                                />

                                <MetricInput
                                    label={t('partnerMaxWidth')}
                                    value={defaultPartnerSettings.incoming.maxWidth}
                                    divisionFactor={10}
                                    onChange={(event) => this.onChangeValue('incoming.maxWidth', event)}
                                    append='cm'
                                />

                                <MetricInput
                                    label={t('partnerMaxHeight')}
                                    value={defaultPartnerSettings.incoming.maxHeight}
                                    divisionFactor={10}
                                    onChange={(event) => this.onChangeValue('incoming.maxHeight', event)}
                                    append='cm'
                                />
                            </FormRow>

                            <FormRow>
                                <MetricInput
                                    label={t('partnerMaxWeight')}
                                    value={defaultPartnerSettings.incoming.maxWeight}
                                    divisionFactor={1000}
                                    onChange={(event) => this.onChangeValue('incoming.maxWeight', event)}
                                    append='kg'
                                />

                                <MetricInput
                                    label={t('partnerMaxVolume')}
                                    value={defaultPartnerSettings.incoming.maxVolume}
                                    onChange={(event) => this.onChangeValue('incoming.maxVolume', event)}
                                    append='dm3'
                                />
                            </FormRow>
                        </FormStack>

                        {this.renderConditions('incoming')}
                    </>
                }

                <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 90}}>
                    <div style={{flex: 1}} />

                    <Button onClick={this.onSubmit.bind(this)} disabled={!this.state.settingsChanged}>
                        {t('save')}
                    </Button>
                </div>
            </Panel>
        )
    }
}
